import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import {
  Modal,
  Form,
  Switch,
  Skeleton,
  Popover,
  Alert,
  Divider,
  Typography,
  Button,
} from 'antd';

import { useWindowSize } from '@/hooks';

import { applyMask } from '@/utils/mask';
import { cepMask, cnpjMask } from '@/utils/mask/commonMasks';

import {
  useActiveOrInactivateCompany,
  useCompany,
  useDeleteCompany,
} from '@/core/domain/enterprise/Companies/Companies.hooks';

import { PageProps } from '@/types/PageProps';
import CompanyGeneralInfoForm from '@/components/Companies/CompanyGeneralInfoForm';
import { DeleteButton, SaveButton } from '@/components/_common/Buttons';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import { useCompanyFormContext } from '@/components/Companies/CompaniesFormProvider';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { UploadFile } from 'antd/es/upload/interface';
import { v4 } from 'uuid';
import { CompanyUpdateForm } from '@/core/domain/enterprise/Companies/Companies.types';
import CompanyFooter from '@/components/Companies/CompanyFooter';
import Authorities from '@/types/Authority';
import useSession, { AuthModule } from '@/hooks/useSession';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import StatusTag from '@/components/_common/StatusTag';
import { Group } from '@/components/_common';
import { getUserFromLocalStorage } from '@/utils/user';
import { APP_NAME } from '@/index';

const CompanyGeneralInfoPage = ({ route }: PageProps) => {
  const localStorage = getUserFromLocalStorage();
  const accessToken = useSession();
  const intl = useIntl();
  const history = useHistory();

  const { setParamLabel } = useBreadcrumbs();
  const [active, setActive] = useState(true);
  const { isMobile } = useWindowSize();

  const { isLoading, companyId, forms, disabled } = useCompanyFormContext();

  const [form] = Form.useForm<CompanyUpdateForm>();

  const { data: company, isLoading: isLoadingCompanyDetail } = useCompany(
    +companyId,
  );

  const isBlocked = !!company?.blocked;

  const deleteCompany = useDeleteCompany();
  const activateOrInactivateMutation = useActiveOrInactivateCompany();

  useEffect(() => {
    if (company) {
      if (
        !forms.companyGeneral ||
        (!!forms.companyGeneral && form.getFieldValue('id') !== company.id)
      ) {
        form.setFieldsValue({
          ...company,
          logo: company.logo
            ? [{ name: company.logo ?? '', uid: '', url: company.logo ?? '' }]
            : null,
          cnpj: applyMask(company.cnpj, cnpjMask),
          companyAddress: {
            ...company?.companyAddress,
            zip: applyMask(company?.companyAddress?.zip, cepMask),
          },
          contactList: [...company?.contactList],
        });

        setActive(company.active);

        if (company.logo) {
          const img: UploadFile = {
            uid: v4(),
            name: company.logo.toString(),
            url: company.logo.toString(),
            status: 'done',
          };

          form.setFieldsValue({ logo: [img] });
        }
      }

      setParamLabel(':companyId', company.name);
    }
  }, [company]);

  const handleUpdateClick = async () => {
    await form.validateFields();
    form.submit();
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'companies.remove' }),
      content: `${intl.formatMessage({
        id: 'companies.remove.alert',
      })} ${company?.name}`,
      onOk: async () => {
        await deleteCompany.mutateAsync(+companyId);

        history.push('/app/registry/companies');
      },
      okType: 'danger',
    });
  };

  const handleActiveClick = async (active: boolean) => {
    await activateOrInactivateMutation.mutateAsync(Number(companyId));

    setActive(active);
  };

  const hasReachedCompanyLimit =
    (company?.totalCompanies || 0) >= (company?.maxCompanies || 0);

  const openWhatsApp = (number: string) => {
    const message = `Olá, preciso de ajuda com o desbloqueio da empresa.\n\nEmpresa: ${
      company?.name
    }\nCNPJ: ${applyMask(company?.cnpj, cnpjMask)}\n\nCódigo do cliente: ${
      localStorage.matrixId
    }`;

    const url = isMobile
      ? `https://wa.me/55${number}?text=${encodeURIComponent(message)}`
      : `https://web.whatsapp.com/send?phone=55${number}&text=${encodeURIComponent(
          message,
        )}`;

    window.open(url, '_blank');
  };

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={route.parent}
      baseKey={String(companyId)}
      basePath={`/app/registry/companies/${companyId}`}
      extra={
        <SpaceForButton flexDirection={isMobile ? 'column-reverse' : 'row'}>
          {!isBlocked && (
            <Switch
              unCheckedChildren={intl.formatMessage({
                id: 'commons.entity.inactive',
              })}
              checkedChildren={intl.formatMessage({
                id: 'commons.entity.active',
              })}
              loading={isLoading || activateOrInactivateMutation.isLoading}
              checked={active}
              onChange={handleActiveClick}
              disabled={
                !hasPermissions([Authorities.REGISTER_COMPANY_EDIT]) ||
                accessToken?.module === AuthModule.ON ||
                isLoadingCompanyDetail
              }
            />
          )}

          {isBlocked && (
            <Popover
              content={
                hasReachedCompanyLimit && (
                  <>
                    <strong>Limite de empresas</strong>
                    <p>
                      Para desbloquear entre em contato com o suporte da
                      plataforma {APP_NAME}.
                    </p>
                  </>
                )
              }
            >
              <div>
                <StatusTag label="Bloqueado" color="red" />
              </div>
            </Popover>
          )}

          <DeleteButton
            block
            onClick={handleRemoveClick}
            disabled={
              !hasPermissions([Authorities.REGISTER_COMPANY_DELETE]) ||
              accessToken?.module === AuthModule.ON
            }
          >
            <FormattedMessage id="commons.entity.remove" />
          </DeleteButton>

          <SaveButton
            block
            loading={isLoading}
            type="primary"
            disabled={
              disabled || !hasPermissions([Authorities.REGISTER_COMPANY_EDIT])
            }
            onClick={handleUpdateClick}
          >
            <FormattedMessage id="commons.entity.save" />
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Skeleton loading={isLoadingCompanyDetail}>
        {!!company?.blocked && (
          <>
            <Alert
              type="warning"
              showIcon
              message="Empresa bloqueada."
              description={
                <>
                  {hasReachedCompanyLimit && (
                    <Typography.Text>
                      Limite máximo de empresas atingido (
                      {company.totalCompanies} de {company.maxCompanies}).{' '}
                    </Typography.Text>
                  )}
                  <Typography.Text>
                    Para desbloquear, entre em contato com o nosso time de
                    suporte:
                  </Typography.Text>

                  <Group>
                    <Button
                      type="link"
                      onClick={() => openWhatsApp('31991344373')}
                    >
                      <Typography.Text strong>(31) 99134-4373</Typography.Text>
                    </Button>

                    <Divider type="vertical" />

                    <Button
                      type="link"
                      onClick={() => openWhatsApp('31994921769')}
                    >
                      <Typography.Text strong>(31) 99492-1769</Typography.Text>
                    </Button>
                  </Group>
                </>
              }
            />

            <Divider />
          </>
        )}

        <CompanyGeneralInfoForm form={form} address={company?.companyAddress} />

        <CompanyFooter disabled={disabled} />
      </Skeleton>
    </DefaultRouteTabs>
  );
};

export default CompanyGeneralInfoPage;
